import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Heart } from "lucide-react";
import { CONTENT } from "@/content";

// Build a horizontal ECG waveform path across a 0..1000 x 0..120 viewBox.
const BASE_Y = 60;
const beat = (x) =>
  `L${x + 40},${BASE_Y} L${x + 50},${BASE_Y - 8} L${x + 60},${BASE_Y} ` +
  `L${x + 66},${BASE_Y + 8} L${x + 74},${BASE_Y - 42} L${x + 82},${BASE_Y + 30} ` +
  `L${x + 90},${BASE_Y}`;
const ECG_PATH =
  `M0,${BASE_Y} ` + [40, 380, 720].map(beat).join(" ") + ` L1000,${BASE_Y}`;

export default function VideoSection() {
  const { heading, note, clips } = CONTENT.video;
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const clip = clips[0];

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      const p = v.play();
      if (p && typeof p.then === "function") {
        p.then(() => setPlaying(true)).catch(() => setPlaying(false));
      } else {
        setPlaying(true);
      }
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  return (
    <section className="px-6 md:px-16 py-28 md:py-40" data-testid="video-section">
      <div className="mb-14 max-w-2xl">
        <span className="font-body text-xs uppercase tracking-[0.4em]" style={{ color: "var(--gold)" }}>
          a moving picture
        </span>
        <h2 className="font-serif-d tracking-tightest text-5xl md:text-7xl font-light mt-4">
          {heading}
        </h2>
        <p className="font-body mt-4 text-sm" style={{ color: "var(--muted-rose)" }}>
          {note}
        </p>
      </div>

      <div className="flex flex-col items-center gap-8 md:gap-10">
        {/* portrait frame, sized to the video */}
        <motion.div
          className="relative rounded-[1.5rem] overflow-hidden glow-rose"
          style={{
            width: "min(78vw, 330px)",
            aspectRatio: "480 / 854",
            border: "1px solid rgba(232,180,184,0.18)",
          }}
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1 }}
        >
          <video
            ref={videoRef}
            src={clip.src}
            poster={clip.poster}
            className="w-full h-full object-cover bg-black"
            onEnded={() => setPlaying(false)}
            data-testid="memory-video"
            preload="metadata"
            playsInline
          />
          <button
            onClick={toggle}
            data-testid="video-play-button"
            className="absolute inset-0 flex items-center justify-center group"
            aria-label={playing ? "Pause" : "Play"}
          >
            <motion.span
              className="flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full glass"
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.95 }}
              animate={{ opacity: playing ? 0 : 1 }}
            >
              {playing ? (
                <Pause size={26} style={{ color: "var(--alabaster)" }} />
              ) : (
                <Play size={26} style={{ color: "var(--alabaster)", marginLeft: 3 }} />
              )}
            </motion.span>
          </button>
        </motion.div>

        {/* ECG heartbeat line running into the beating heart */}
        <motion.div
          className="w-full max-w-4xl flex items-center gap-2 md:gap-4"
          data-testid="heartbeat-row"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.2 }}
        >
          <svg
            viewBox="0 0 1000 120"
            preserveAspectRatio="none"
            className="flex-1 h-14 md:h-20"
            data-testid="heartbeat-line"
            aria-hidden="true"
          >
            {/* dim base trace */}
            <path
              d={ECG_PATH}
              fill="none"
              stroke="rgba(232,180,184,0.22)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
            {/* glowing travelling pulse */}
            <path
              className="ecg-pulse"
              d={ECG_PATH}
              pathLength="1"
              fill="none"
              stroke="var(--gold)"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          </svg>

          {/* beating heart at the end of the line */}
          <div
            className="relative flex items-center justify-center w-16 h-16 md:w-24 md:h-24 shrink-0"
            data-testid="video-heart"
          >
            {[0, 1].map((i) => (
              <motion.span
                key={i}
                className="absolute rounded-full"
                style={{ width: 60, height: 60, border: "1px solid rgba(232,180,184,0.4)" }}
                animate={{ scale: [1, 1.9], opacity: [0.55, 0] }}
                transition={{ duration: 2.8, repeat: Infinity, delay: i * 1.4, ease: "easeOut" }}
              />
            ))}
            <motion.div
              className="glow-rose"
              animate={{ scale: [1, 1, 1.26, 1.05, 1] }}
              transition={{
                duration: 2.8,
                times: [0, 0.72, 0.85, 0.93, 1],
                repeat: Infinity,
                ease: "easeOut",
              }}
            >
              <Heart
                className="w-11 h-11 md:w-16 md:h-16"
                strokeWidth={1}
                style={{ color: "var(--rose)" }}
                fill="var(--rose)"
              />
            </motion.div>
          </div>
        </motion.div>

        <span className="font-script text-2xl" style={{ color: "var(--rose)" }}>
          beating, always
        </span>
      </div>
    </section>
  );
}
